export const DIGITAL_LIBRARY_BASE_URL = 'https://delivery.digitallibrary.zillowgroup.com/public';

export const SPOT_ILLUSTRATION_NAMES = [
  '3d-floorplan',
  '3d-tours',
  'accomplishment',
  'agent-buyer-handshake',
  'agents',
  'announcement',
  'apartment-balcony',
  'apartment-elevator-entry',
  'apartment-interior-couch',
  'apartment-interior-desk',
  'apartment-laundry-room',
  'apartment-pool',
  'apartment-rooftop-grill',
  'apartment-tour',
  'app-announcement',
  'application',
  'approve',
  'avoid-mistakes',
  'bedroom',
  'billboard-ad',
  'buyer-education',
  'calculator',
  'calculator-paperwork',
  'calendar-clock',
  'calendar-money',
  'calendar-pay-rent',
  'calendar-schedule',
  'call',
  'car',
  'cat',
  'celebrate',
  'celebrate-zillow',
  'chart-trending-up',
  'checklist',
  'city-skyline',
  'claim-properties',
  'client-insights',
  'clipboard',
  'collections',
  'communication-finance',
  'communication-virtual',
  'community-outdoors',
  'compare-homes',
  'condition-report',
  'confirmation',
  'contact',
  'contract-finance',
  'core-value-better-together-2025',
  'core-value-consumers-are-our-north-star-2025',
  'core-value-do-the-right-thing-2025',
  'core-value-own-it-2025',
  'core-value-turn-on-the-lights-2025',
  'core-value-we-play-to-win-2025',
  'credit-score',
  'device-finance',
  'document-homes',
  'document-medical',
  'document-notarized',
  'document-option1',
  'document-option2',
  'document-signature',
  'document-verified',
  'documents-option1',
  'documents-option2',
  'envelope-empty',
  'error',
  'fee-transparency',
  'filters',
  'finance',
  'finance-option2',
  'finance-option3',
  'financial-education',
  'folder',
  'for-sale',
  'gavel',
  'guided-steps',
  'gym',
  'handshake',
  'heart',
  'home-data-laptop',
  'home-manage-properties',
  'home-midwest-style',
  'home-rental',
  'homeowner-dashboard',
  'house-checkmark',
  'house-info',
  'house-savings',
  'idea',
  'integration',
  'interest-rates',
  'interest-rates-lowering',
  'key',
  'key-move-in',
  'laptop',
  'laptop-browsing',
  'laptop-chart',
  'laptop-phone',
  'listing-media',
  'magnifying-glass',
  'magnifying-glass-users',
  'mailbox',
  'maintenance',
  'map',
  'marketing-flexibility',
  'messaging',
  'moving-box',
  'multi-family-apartment-building',
  'multi-family-building',
  'network',
  'newconstruction-build',
  'notification-homes',
  'nurture',
  'online-payment',
  'people',
  'people-confirmation',
  'pet',
  'pet-friendly-apartment',
  'phone-notification',
  'photo-carousel',
  'photographer',
  'photography',
  'post-home-listing',
  'post-rental-listing',
  'price-range',
  'professional-paperwork',
  'professional-talking',
  'rental-dashboard',
  'rental-data',
  'rental-data-laptop',
  'rental-listing',
  'rental-listing-map',
  'rental-manage-properties',
  'rental-notifications',
  'rental-occupancy',
  'rental-search',
  'rental-tour',
  'renter-moving',
  'renter-shopping',
  'roi',
  'roi-data',
  'save-time-and-money',
  'saved-homes',
  'search-homes',
  'social-media',
  'star-rising',
  'support',
  'target',
  'tasks',
  'team',
  'temporary-living-costs',
  'touring',
  'trophy',
  'user-idea',
  'user-info',
  'user-question',
  'verify',
  'webpage',
  'welcome-home',
  'zillow-ad',
  'zillow-ads',
  'zillow-phone',
  'zipcode',
] as const;

export const SCENE_ILLUSTRATION_NAMES = [
  'applying-home',
  'buy-home',
  'buy-home-people',
  'buyability',
  'customer-support',
  'family',
  'finance-home',
  'moving',
  'rent-home',
  'rent-home-people',
  'sell-home',
  'sell-home-yourself',
  'team-collaboration',
  'team-collaboration-maintenance',
  'team-welcome',
  'touring-people',
] as const;

export type SpotIllustrationName = (typeof SPOT_ILLUSTRATION_NAMES)[number];

export type SceneIllustrationName = (typeof SCENE_ILLUSTRATION_NAMES)[number];

export type SpotIllustrationTone = 'light' | 'dark';

/** Names the Digital Library has only a light file for; they render the light file in dark mode too until a dark file is published. */
export const LIGHT_ONLY_ILLUSTRATION_NAMES: ReadonlySet<string> = new Set(['team-collaboration-maintenance']);

export function hasDarkIllustration(name: string): boolean {
  return !LIGHT_ONLY_ILLUSTRATION_NAMES.has(name);
}

function getDigitalLibrarySvgUrl(name: string, tone: SpotIllustrationTone): string {
  const fileTone = hasDarkIllustration(name) ? tone : 'light';
  return `${DIGITAL_LIBRARY_BASE_URL}/${name}-${fileTone}_svg_Original.svg`;
}

export function getSpotIllustrationUrl(name: SpotIllustrationName, tone: SpotIllustrationTone = 'light'): string {
  return getDigitalLibrarySvgUrl(name, tone);
}

export function getSceneIllustrationUrl(name: SceneIllustrationName, tone: SpotIllustrationTone = 'light'): string {
  return getDigitalLibrarySvgUrl(name, tone);
}

/**
 * Search keywords for each illustration, copied from Constellation's illustration finder
 * (https://constellation.zillowgroup.com/foundations/illustrations?tabs=finder) on 2026-10-28. Names the finder
 * gives no keywords are left out.
 */
export const ILLUSTRATION_KEYWORDS: Readonly<Record<string, ReadonlyArray<string>>> = {
  '3d-floorplan': ['virtual'],
  '3d-tours': ['virtual', 'phone', 'app'],
  accomplishment: ['success', 'journey'],
  'agent-buyer-handshake': ['people', 'handshake'],
  agents: ['people', 'talking', 'application'],
  announcement: ['megaphone'],
  'apartment-interior-couch': ['multi-family home', 'building', 'rental', 'city'],
  'apartment-interior-desk': ['multi-family home', 'building', 'rental', 'city'],
  'apartment-rooftop-grill': ['multi-family home', 'building', 'rental', 'city', 'exterior', 'outside'],
  'apartment-tour': ['building', 'couple', 'app'],
  'app-announcement': ['phone', 'megaphone'],
  application: ['file', 'folder', 'paperwork', 'complete', 'checkmark'],
  approve: ['checkmark', 'thumbs up'],
  'avoid-mistakes': ['error', 'stop'],
  bedroom: ['interior'],
  'billboard-ad': ['city'],
  'buyer-education': ['online', 'search', 'research', 'computer'],
  'calculator-paperwork': ['document', 'paperwork'],
  'calendar-money': ['finance'],
  'calendar-schedule': ['checkmark', 'complete', 'verified'],
  call: ['person', 'phone'],
  car: ['outside'],
  cat: ['pet'],
  celebrate: ['high five'],
  checklist: ['document', 'paperwork'],
  'city-skyline': ['buildings', 'outside'],
  'claim-properties': ['building', 'checkmark', 'multi-family', 'apartment'],
  'client-insights': ['laptop', 'compare', 'homes'],
  clipboard: ['checkmark', 'complete', 'verified', 'checklist', 'document', 'paperwork'],
  collections: ['photos', 'homes'],
  'communication-finance': ['people', 'talking'],
  'communication-virtual': ['people', 'talking', 'support'],
  'community-outdoors': ['people', 'activity', 'outside'],
  'compare-homes': ['like', 'heart', 'homes', 'photos'],
  'condition-report': ['document'],
  contact: ['agent', 'screen', 'web'],
  'contract-finance': ['signature'],
  'credit-score': ['finance'],
  'device-finance': ['phone', 'mobile'],
  'document-notarized': ['notary', 'seal', 'signed'],
  'document-signature': ['sign', 'lease'],
  'document-verified': ['checkmark', 'complete'],
  'envelope-empty': ['mail'],
  'fee-transparency': ['rental', 'cost'],
  'financial-education': ['research', 'online'],
  folder: ['file', 'paperwork'],
  'for-sale': ['yard sign'],
  gavel: ['legal', 'protections'],
  'guided-steps': ['clipboard', 'document', 'process', 'progress'],
  gym: ['interior', 'inside', 'activity'],
  heart: ['favorite', 'save'],
  'home-data-laptop': ['house', 'laptop', 'computer'],
  'home-manage-properties': ['house', 'phone', 'app', 'listings'],
  'home-midwest-style': ['single family home'],
  'home-rental': ['multi-family home', 'building', 'rental', 'city', 'exterior'],
  'homeowner-dashboard': ['house', 'owner', 'laptop', 'computer'],
  'house-checkmark': ['single family home', 'complete', 'verified'],
  'house-savings': ['finance'],
  'interest-rates': ['phone', 'app'],
  'interest-rates-lowering': ['phone', 'app'],
  key: ['unlock'],
  laptop: ['computer'],
  'laptop-browsing': ['search'],
  'laptop-chart': ['computer'],
  'laptop-phone': ['computer'],
  'listing-media': ['laptop', 'virtual'],
  'magnifying-glass': ['search'],
  'magnifying-glass-users': ['search'],
  mailbox: ['mail'],
  maintenance: ['wrench'],
  map: ['search', 'magnifying glass'],
  'marketing-flexibility': ['dial', 'adjust', 'volume', 'megaphone'],
  messaging: ['phone', 'conversation'],
  'multi-family-building': ['city', 'rental'],
  network: ['people', 'connection'],
  'newconstruction-build': ['new construction', 'building', 'house'],
  'online-payment': ['laptop', 'web'],
  pet: ['dog'],
  'pet-friendly-apartment': ['building', 'dog', 'cat'],
  'phone-notification': ['mobile'],
  'photo-carousel': ['homes', 'compare', 'property'],
  photographer: ['camera'],
  photography: ['camera'],
  'post-home-listing': ['laptop', 'computer'],
  'post-rental-listing': ['laptop', 'computer'],
  'price-range': ['home'],
  'professional-paperwork': ['agent'],
  'professional-talking': ['agent'],
  'rental-dashboard': ['laptop', 'computer'],
  'rental-data': ['chart', 'graph', 'building', 'up', 'trend'],
  'rental-data-laptop': ['building', 'multi-family', 'laptop', 'computer'],
  'rental-listing': ['laptop', 'computer'],
  'rental-listing-map': ['building', 'search'],
  'rental-manage-properties': ['phone', 'app', 'listings'],
  'rental-notifications': ['phone', 'building', 'multi-family'],
  'rental-occupancy': ['chart', 'graph', 'up', 'trend', 'building', 'arrow'],
  'rental-search': ['building', 'multi-family'],
  'rental-tour': ['building', 'city', 'people'],
  'renter-moving': ['building', 'box', 'city'],
  'renter-shopping': ['building', 'app'],
  roi: ['plant', 'coin', 'return on investment'],
  'roi-data': ['dollar', 'finance', 'return on investment'],
  'save-time-and-money': ['finance'],
  'saved-homes': ['single family home', 'heart', 'like'],
  'social-media': ['phone', 'app', 'favorite'],
  'star-rising': ['achievement', 'congratulations'],
  support: ['conversation', 'group', 'people', 'team'],
  target: ['goal', 'aim'],
  tasks: ['checklist', 'to do list', 'document'],
  team: ['people', 'agent', 'group'],
  trophy: ['achievement', 'congratulations'],
  'user-info': ['paper', 'document', 'profile', 'form'],
  'user-question': ['ask'],
  verify: ['shield', 'protection', 'verification'],
  webpage: ['laptop'],
  'welcome-home': ['door', 'entrance'],
  'zillow-ad': ['laptop'],
  'zillow-ads': ['laptop', 'phone', 'app', 'sparkle'],
  'zillow-phone': ['phone', 'mobile'],
  zipcode: ['map', 'location', 'area'],
  'applying-home': ['application', 'computer', 'online', 'desktop'],
  'buy-home': ['single family home', 'exterior', 'car', 'outside'],
  'buy-home-people': ['single family home', 'people', 'car', 'exterior', 'outside', 'sold', 'keys', 'agent', 'buyers'],
  'customer-support': ['online', 'talking', 'interior', 'inside'],
  family: ['people', 'walking', 'outside'],
  'finance-home': ['single family home', 'exterior', 'outside', 'talking', 'for sale', 'finance'],
  moving: ['people', 'interior', 'inside', 'talking'],
  'rent-home': ['multi-family home', 'building', 'rental', 'apartment', 'moving truck', 'exterior', 'outside'],
  'rent-home-people': [
    'multi-family home',
    'building',
    'rental',
    'apartment',
    'moving truck',
    'exterior',
    'outside',
    'people',
  ],
  'sell-home': ['single family home', 'exterior', 'outside', 'talking', 'for sale', 'agent'],
  'sell-home-yourself': ['single family home', 'exterior', 'outside', 'talking', 'for sale by owner'],
};
